import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import { memo } from "react";
import {
  AdvancedTableFilter,
  encodeAdvancedFilters,
} from "../../advanced-filters/advanced-filter";
import { RedoButton } from "../buttons/redo-button";
import { AllFiltersModal } from "./all-filters-modal";
import { FilterChipsList, FilterComponent } from "./filter-chips-list";

export interface TableFiltersModalProps {
  open: boolean;
  onClose: () => void;
  shouldShowFilterButton: boolean;
  shouldShowAdvancedFilterButton: boolean;
  filterComponents: FilterComponent[];
  selectedFilters: string[];
  filters: Record<string, string>;
  onFiltersChange: (filters: Record<string, string>) => void;
  onSelectedFiltersChange: (selectedFilters: string[]) => void;
  displayedAdvancedFilters: AdvancedTableFilter[];
  onAdvancedFiltersChange: (params: string) => void;
  allFiltersMatch: boolean;
  onResetToViewFilters: () => void;
  onAddFilterClick: () => void;
  addFilterButtonRef: ((ref: HTMLButtonElement | null) => void) | undefined;
  AdvancedFilterComponent: React.ComponentType<{
    filter: AdvancedTableFilter;
    filters: AdvancedTableFilter[];
    isViewFilter: boolean;
    removeFilter: () => void;
    setValue: (filter: AdvancedTableFilter) => void;
  }>;
}

export const TableFiltersModal = memo(function TableFiltersModal({
  open,
  onClose,
  shouldShowFilterButton,
  shouldShowAdvancedFilterButton,
  filterComponents,
  selectedFilters,
  filters,
  onFiltersChange,
  onSelectedFiltersChange,
  displayedAdvancedFilters,
  onAdvancedFiltersChange,
  allFiltersMatch,
  onResetToViewFilters,
  onAddFilterClick,
  addFilterButtonRef,
  AdvancedFilterComponent,
}: TableFiltersModalProps) {
  const handleClose = useHandler(() => {
    onClose();
  });

  const handleResetFilters = useHandler(() => {
    onResetToViewFilters();
  });

  return (
    <AllFiltersModal
      addFilterButton={
        shouldShowAdvancedFilterButton ? (
          <RedoButton
            hierarchy="primary"
            IconLeading={FilterLinesIcon}
            onClick={onAddFilterClick}
            ref={addFilterButtonRef}
            text="Add filter"
          />
        ) : undefined
      }
      onClose={handleClose}
      onResetFilters={
        shouldShowAdvancedFilterButton && !allFiltersMatch
          ? handleResetFilters
          : undefined
      }
      open={open}
    >
      {shouldShowFilterButton && (
        <FilterChipsList
          filterComponents={filterComponents}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onSelectedFiltersChange={onSelectedFiltersChange}
          selectedFilters={selectedFilters}
        />
      )}
      {shouldShowAdvancedFilterButton && (
        <IterableMap
          items={displayedAdvancedFilters}
          keyFn={(filter, index) => filter.data.name + index}
        >
          {(filter, index) => {
            function setValue(newFilter: AdvancedTableFilter) {
              const newFilters = displayedAdvancedFilters.map((f, i) =>
                index === i ? newFilter : f,
              );
              onAdvancedFiltersChange(encodeAdvancedFilters(newFilters));
            }

            function removeFilter() {
              const newFilters = displayedAdvancedFilters.filter(
                (_, i) => i !== index,
              );
              onAdvancedFiltersChange(encodeAdvancedFilters(newFilters));
            }

            return (
              <AdvancedFilterComponent
                filter={filter}
                filters={displayedAdvancedFilters}
                isViewFilter
                removeFilter={removeFilter}
                setValue={setValue}
              />
            );
          }}
        </IterableMap>
      )}
    </AllFiltersModal>
  );
});
